import React, { useState, useContext, useEffect, useCallback } from "react";
import {
  Hidden,
  makeStyles,
  Tabs,
  Tab,
  Popover,
  MenuItem,
} from "@material-ui/core";
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import Card from "../../components/Card/Card.js";
import CardBody from "../../components/Card/CardBody.js";
import ActionBar from "../../components/ActionBar/ActionBar.js";
import ActionBarButton from "../../components/ActionBar/ActionBarButton.js";
import AddIcon from "@material-ui/icons/Add";
import ListAltIcon from "@material-ui/icons/ListAlt";
import { ScenarioCreationDrawer } from "./viewComponents/scenarioCreation/ScenarioCreationDrawer";
import ScenarioMaskModal from "./viewComponents/scenarioCreation/ScenarioMaskModal.js";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import AuthContext from "connections/auth/AuthProvider.js";
import { useTranslation } from "react-i18next";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import {
  useScenarioDataContext,
  ScenarioDataProvider,
} from "../../services/context/ScenarioDataContext.js";

import BoardTab from "./viewComponents/BoardTab/BoardTab.js";
import FinancialImpactTab from "./viewComponents/FinancialImpactTab/FinancialImpactTab.js";
import PriceRouteTab from "./viewComponents/PriceRouteTab/PriceRouteTab.js";
import ComparativeTab from "./viewComponents/ComparativeTab/ComparativeTab.js";
import FloatingStatus from "components/FloatingStatus/FloatingStatus.js";
import { ScenarioManagerModal } from "./viewComponents/scenarioManager";
import FolderCreateDialog from "./viewComponents/scenarioManager/FolderCreateDialog";
import { whiteColor } from "assets/jss/material-dashboard-pro-react.js";
import {
  getFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  updateScenario,
  archiveScenarios,
  deleteScenario,
} from "./viewServices/calls/apiCalls";
import { toast } from "react-toastify";

const cockpitStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  actionIcon: {
    color: clientColor,
  },
  newScenarioButtonIconWrapper: {
    border: `1px solid ${clientColor}`,
  },
  tabsCard: {
    margin: 0,
  },
  tabsCardBody: {
    padding: 0,
  },
  selectScenarioCard: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  selectFormControl: {
    marginBottom: theme.spacing(1),
    minWidth: 200,
    margin: "7px 0 7px 0 !important",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  selectScenarioBox: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(2),
    marginLeft: theme.spacing(2),
  },
  viewButton: {
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  firstButton: {
    fontSize: "0.7rem",
    borderTopRightRadius: "0",
    borderBottomRightRadius: "0",
    borderColor: clientColor,
    margin: "0",
    position: "relative",
    float: "left",
    "&:hover": {
      zIndex: "2",
    },
  },
  lastButton: {
    fontSize: "0.7rem",
    borderTopLeftRadius: "0",
    borderBottomLeftRadius: "0",
    borderColor: clientColor,
    margin: "0",
    "&:hover": {
      zIndex: "2",
    },
  },
  buttonSelected: {
    color: "white",
    backgroundColor: clientColor,
    "&:hover": {
      backgroundColor: clientColor,
    },
  },
  topButtons: {
    position: "relative",
    display: "inline-block",
    verticalAlign: "middle",
    marginRight: theme.spacing(2),
  },
  tabs: {
    "& .MuiTab-root": {
      backgroundColor: grayColor[13],
      opacity: 0.7,
      "&.Mui-selected": {
        backgroundColor: whiteColor,
        color: clientColor,
        opacity: 1,
      },
    },
    "& .MuiTabs-indicator": {
      backgroundColor: clientColor,
    },
  },
  tabContentWrapper: {
    height: "calc(100vh - 163px)", // Account for header, action bar, tabs, and footer (140px + 23px)
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
});

const CockpitMainContent = () => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles((theme) => cockpitStyle(theme, clientColor));
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // Use the scenario data context
  const {
    scenarios,
    allScenarios,
    getRecentlyCompletedScenario,
    clearRecentlyCompletedScenario,
    refreshData,
    enablePolling,
    updateScenarioLocally,
    removeScenarioLocally,
    restoreScenarioLocally,
  } = useScenarioDataContext();

  // Local component state - no persistence, clears on refresh
  const [activeTab, setActiveTab] = useState(0);
  const [isNewScenarioModalOpen, setIsNewScenarioModalOpen] = useState(false);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);
  const [isMaskModalOpen, setIsMaskModalOpen] = useState(false);
  const [selectedScenarioForMask, setSelectedScenarioForMask] = useState(null);
  const [scenarioToOpenInBoard, setScenarioToOpenInBoard] = useState(null);
  const [newScenarioAnchorEl, setNewScenarioAnchorEl] = useState(null);
  const [folders, setFolders] = useState([]);
  const [isFolderCreateDialogOpen, setIsFolderCreateDialogOpen] =
    useState(false);
  const [folderCreateParentId, setFolderCreateParentId] = useState(null);

  // Fetch folders on mount and when refreshing
  const fetchFolders = useCallback(async () => {
    try {
      const foldersData = await getFolders();
      setFolders(foldersData);
    } catch (err) {
      console.error("Failed to fetch folders:", err);
    }
  }, []);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // Helper functions for optimistic folder updates
  const updateFolderInTree = useCallback((foldersArray, folderId, updates) => {
    return foldersArray.map((folder) => {
      if (folder.folder_id === folderId) {
        return { ...folder, ...updates };
      }
      if (folder.subfolders?.length > 0) {
        return {
          ...folder,
          subfolders: updateFolderInTree(folder.subfolders, folderId, updates),
        };
      }
      return folder;
    });
  }, []);

  const removeFolderFromTree = useCallback((foldersArray, folderId) => {
    return foldersArray
      .filter((folder) => folder.folder_id !== folderId)
      .map((folder) => ({
        ...folder,
        subfolders: folder.subfolders
          ? removeFolderFromTree(folder.subfolders, folderId)
          : [],
      }));
  }, []);

  const addFolderToTree = useCallback(
    (foldersArray, newFolder, parentFolderId) => {
      if (!parentFolderId) {
        // Add to root level
        return [...foldersArray, newFolder];
      }
      return foldersArray.map((folder) => {
        if (folder.folder_id === parentFolderId) {
          return {
            ...folder,
            subfolders: [...(folder.subfolders || []), newFolder],
          };
        }
        if (folder.subfolders?.length > 0) {
          return {
            ...folder,
            subfolders: addFolderToTree(
              folder.subfolders,
              newFolder,
              parentFolderId,
            ),
          };
        }
        return folder;
      });
    },
    [],
  );

  const moveFolderInTree = useCallback(
    (foldersArray, folderId, newParentId) => {
      // Find and remove the folder from its current position
      let folderToMove = null;
      const findAndRemove = (folders) => {
        return folders
          .filter((f) => {
            if (f.folder_id === folderId) {
              folderToMove = f;
              return false;
            }
            return true;
          })
          .map((f) => ({
            ...f,
            subfolders: f.subfolders ? findAndRemove(f.subfolders) : [],
          }));
      };

      const treeWithoutFolder = findAndRemove(foldersArray);

      if (!folderToMove) return foldersArray;

      // Add to new parent
      return addFolderToTree(treeWithoutFolder, folderToMove, newParentId);
    },
    [addFolderToTree],
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // New Scenario popover handlers
  const handleNewScenarioClick = (event) => {
    setNewScenarioAnchorEl(event.currentTarget);
  };

  const handleCloseNewScenarioPopover = () => {
    setNewScenarioAnchorEl(null);
  };

  const handleFromInput = () => {
    handleCloseNewScenarioPopover();
    setIsNewScenarioModalOpen(true);
  };

  const handleFromExisting = () => {
    handleCloseNewScenarioPopover();
    handleOpenMaskModal(null);
  };

  const handleCloseNewScenarioModal = useCallback(
    (shouldRefresh = false) => {
      setIsNewScenarioModalOpen(false);
      if (shouldRefresh) {
        // Trigger refresh and start polling for new scenario
        refreshData();
        enablePolling(); // Enable polling when new scenario is created
      }
    },
    [refreshData, enablePolling],
  );

  const handleOpenManagerModal = () => {
    fetchFolders(); // Refresh folders when opening
    setIsManagerModalOpen(true);
  };

  const handleCloseManagerModal = () => {
    setIsManagerModalOpen(false);
    clearRecentlyCompletedScenario();
  };

  // ScenarioManagerModal handlers - all optimistic for instant UI feedback
  const handleScenarioMove = async (scenarioId, folderId) => {
    // Find the scenario to get its previous folder_id for rollback
    const scenario = allScenarios.find((s) => s.scenario_id === scenarioId);
    const previousFolderId = scenario?.folder_id;

    // Optimistic update
    updateScenarioLocally(scenarioId, { folder_id: folderId });

    try {
      await updateScenario(scenarioId, { folder_id: folderId });
    } catch (err) {
      console.error("Failed to move scenario:", err);
      // Rollback on failure
      updateScenarioLocally(scenarioId, { folder_id: previousFolderId });
      toast.error(t("scenario_manager.toast.move_failed"));
    }
  };

  const handleFolderMove = async (folderId, newParentId) => {
    // Store previous state for rollback
    const previousFolders = folders;

    // Optimistic update
    setFolders((prev) => moveFolderInTree(prev, folderId, newParentId));

    try {
      await updateFolder(folderId, { parent_folder_id: newParentId });
    } catch (err) {
      console.error("Failed to move folder:", err);
      // Rollback on failure
      setFolders(previousFolders);
      toast.error(t("scenario_manager.toast.move_failed"));
    }
  };

  const handleScenarioUpdate = async (scenarioId, updates) => {
    // Find the scenario to get previous values for rollback
    const scenario = allScenarios.find((s) => s.scenario_id === scenarioId);
    const previousValues = {};
    Object.keys(updates).forEach((key) => {
      previousValues[key] = scenario?.[key];
    });

    // Optimistic update
    updateScenarioLocally(scenarioId, updates);

    try {
      await updateScenario(scenarioId, updates);
    } catch (err) {
      console.error("Failed to update scenario:", err);
      // Rollback on failure
      updateScenarioLocally(scenarioId, previousValues);
      toast.error(t("scenario_manager.toast.update_failed"));
    }
  };

  const handleScenarioArchive = async (scenarioId) => {
    // Find the scenario for rollback
    const scenario = allScenarios.find((s) => s.scenario_id === scenarioId);

    // Optimistic update
    updateScenarioLocally(scenarioId, { is_archived: true });
    toast.success(t("scenario_manager.toast.scenario_archived"));

    try {
      await archiveScenarios([scenarioId], true);
    } catch (err) {
      console.error("Failed to archive scenario:", err);
      // Rollback on failure
      updateScenarioLocally(scenarioId, { is_archived: scenario?.is_archived });
      toast.error(t("scenario_manager.toast.scenario_archive_failed"));
    }
  };

  const handleScenarioRestore = async (scenarioId) => {
    // Find the scenario for rollback
    const scenario = allScenarios.find((s) => s.scenario_id === scenarioId);

    // Optimistic update
    updateScenarioLocally(scenarioId, { is_archived: false });
    toast.success(t("scenario_manager.toast.scenario_restored"));

    try {
      await archiveScenarios([scenarioId], false);
    } catch (err) {
      console.error("Failed to restore scenario:", err);
      // Rollback on failure
      updateScenarioLocally(scenarioId, { is_archived: scenario?.is_archived });
      toast.error(t("scenario_manager.toast.scenario_restore_failed"));
    }
  };

  const handleScenarioDelete = async (scenarioId) => {
    // Find the scenario for rollback
    const scenario = allScenarios.find((s) => s.scenario_id === scenarioId);

    // Optimistic update
    removeScenarioLocally(scenarioId);
    toast.success(t("scenario_manager.toast.scenario_deleted"));

    try {
      await deleteScenario(scenarioId);
    } catch (err) {
      console.error("Failed to delete scenario:", err);
      // Rollback on failure
      if (scenario) {
        restoreScenarioLocally(scenario);
      }
      toast.error(t("scenario_manager.toast.scenario_delete_failed"));
    }
  };

  const handleScenarioOpen = (scenarioId) => {
    // Close the manager modal and select the scenario in the board tab
    setIsManagerModalOpen(false);
    setScenarioToOpenInBoard(scenarioId);
    setActiveTab(0); // Navigate to Scenario Board tab
  };

  const handleFolderCreate = (parentFolderId) => {
    setFolderCreateParentId(parentFolderId);
    setIsFolderCreateDialogOpen(true);
  };

  const handleFolderCreateConfirm = async (folderName) => {
    // Create a temporary folder for optimistic update
    const tempFolderId = `temp-${Date.now()}`;
    const tempFolder = {
      folder_id: tempFolderId,
      name: folderName,
      parent_folder_id: folderCreateParentId,
      subfolders: [],
      scenario_count: 0,
    };

    // Optimistic update
    setFolders((prev) =>
      addFolderToTree(prev, tempFolder, folderCreateParentId),
    );
    toast.success(t("scenario_manager.toast.folder_created"));
    setIsFolderCreateDialogOpen(false);
    const parentId = folderCreateParentId;
    setFolderCreateParentId(null);

    try {
      // API call will return the real folder with proper ID
      await createFolder(folderName, parentId, null);
      // Refresh to get the real folder ID
      fetchFolders();
    } catch (err) {
      console.error("Failed to create folder:", err);
      // Rollback on failure
      setFolders((prev) => removeFolderFromTree(prev, tempFolderId));
      toast.error(t("scenario_manager.toast.folder_create_failed"));
    }
  };

  const handleFolderCreateDialogClose = () => {
    setIsFolderCreateDialogOpen(false);
    setFolderCreateParentId(null);
  };

  const handleFolderDelete = async (folderId) => {
    // Store previous state for rollback
    const previousFolders = folders;

    // Optimistic update - remove folder from tree
    setFolders((prev) => removeFolderFromTree(prev, folderId));
    toast.success(t("scenario_manager.toast.folder_deleted"));

    try {
      await deleteFolder(folderId);
      // Refresh scenarios as they may have been deleted with the folder
      refreshData();
    } catch (err) {
      console.error("Failed to delete folder:", err);
      // Rollback on failure
      setFolders(previousFolders);
      toast.error(t("scenario_manager.toast.folder_delete_failed"));
    }
  };

  const handleFolderRename = async (folderId, newName) => {
    // Store previous state for rollback
    const previousFolders = folders;

    // Helper to update folder name in tree
    const updateFolderName = (foldersArray, targetId, name) => {
      return foldersArray.map((folder) => {
        if (folder.folder_id === targetId) {
          return { ...folder, name };
        }
        if (folder.subfolders?.length > 0) {
          return {
            ...folder,
            subfolders: updateFolderName(folder.subfolders, targetId, name),
          };
        }
        return folder;
      });
    };

    // Optimistic update
    setFolders((prev) => updateFolderName(prev, folderId, newName));

    try {
      await updateFolder(folderId, { name: newName });
      toast.success(t("scenario_manager.toast.folder_renamed"));
    } catch (err) {
      console.error("Failed to rename folder:", err);
      // Rollback on failure
      setFolders(previousFolders);
      toast.error(t("scenario_manager.toast.folder_rename_failed"));
    }
  };

  const handleOpenMaskModal = (scenario) => {
    setSelectedScenarioForMask(scenario);
    setIsMaskModalOpen(true);
  };

  const handleCloseMaskModal = useCallback(
    (shouldRefresh = false) => {
      setIsMaskModalOpen(false);
      setSelectedScenarioForMask(null);
      if (shouldRefresh) {
        refreshData();
        enablePolling();
      }
    },
    [refreshData, enablePolling],
  );

  // Clear recently completed scenario flag when scenarios update (no auto-selection)
  useEffect(() => {
    const recentlyCompleted = getRecentlyCompletedScenario();
    if (recentlyCompleted && scenarios.length > 0) {
      const completedScenarioInList = scenarios.find(
        (s) => s.scenario_id === recentlyCompleted.scenario_id,
      );
      if (completedScenarioInList) {
        // Just clear the flag, don't auto-select
        clearRecentlyCompletedScenario();
      }
    }
  }, [scenarios, getRecentlyCompletedScenario, clearRecentlyCompletedScenario]);

  const renderTabContent = () => {
    // Render all tabs but hide inactive ones to preserve state during navigation
    return (
      <div className={classes.tabContentWrapper}>
        <div
          style={{
            display: activeTab === 0 ? "flex" : "none",
            height: "100%",
            flexDirection: "column",
          }}
        >
          <BoardTab
            scenarios={scenarios}
            folders={folders}
            externalSelectedScenarioId={scenarioToOpenInBoard}
            onExternalScenarioHandled={() => setScenarioToOpenInBoard(null)}
          />
        </div>
        <div
          style={{
            display: activeTab === 1 ? "flex" : "none",
            height: "100%",
            flexDirection: "column",
          }}
        >
          <PriceRouteTab scenarios={scenarios} folders={folders} />
        </div>
        <div
          style={{
            display: activeTab === 2 ? "flex" : "none",
            height: "100%",
            flexDirection: "column",
          }}
        >
          <FinancialImpactTab scenarios={scenarios} folders={folders} />
        </div>
        <div
          style={{
            display: activeTab === 3 ? "flex" : "none",
            height: "100%",
            flexDirection: "column",
          }}
        >
          <ComparativeTab scenarios={scenarios} folders={folders} />
        </div>
      </div>
    );
  };

  const cockpitActions = (
    <>
      <ActionBarButton
        icon={<AddIcon className={classes.actionIcon} />}
        text={t("actionbar.newScenario")}
        onClickHandler={handleNewScenarioClick}
        iconWrapperClassName={classes.newScenarioButtonIconWrapper}
      />
      <ActionBarButton
        icon={<ListAltIcon />}
        text={t("actionbar.scenarioDetails")}
        onClickHandler={handleOpenManagerModal}
      />
      <FloatingStatus />
    </>
  );

  return (
    <>
      <div>
        <Hidden smDown>
          <ActionBar actions={cockpitActions} spinnerIn={false} />
        </Hidden>
        <GridContainer>
          <Hidden mdUp>
            <GridItem xs={12}>
              <ActionBar actions={cockpitActions} spinnerIn={false} sm />
            </GridItem>
          </Hidden>
          <GridItem xs={12}>
            <Card className={classes.tabsCard}>
              <CardBody className={classes.tabsCardBody}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  aria-label="cockpit tabs"
                  variant="fullWidth"
                  className={classes.tabs}
                >
                  <Tab label={t("actionbar.overview")} />
                  <Tab label={t("actionbar.price_route")} />
                  <Tab label={t("actionbar.financial_impact")} />
                  <Tab label={t("actionbar.comparative")} />
                </Tabs>
              </CardBody>
            </Card>
            {renderTabContent()}
          </GridItem>
        </GridContainer>
      </div>

      <ScenarioCreationDrawer
        open={isNewScenarioModalOpen}
        onClose={handleCloseNewScenarioModal}
        scenarios={allScenarios}
      />

      <ScenarioManagerModal
        open={isManagerModalOpen}
        onClose={handleCloseManagerModal}
        folders={folders}
        scenarios={allScenarios}
        onScenarioMove={handleScenarioMove}
        onFolderMove={handleFolderMove}
        onScenarioUpdate={handleScenarioUpdate}
        onScenarioArchive={handleScenarioArchive}
        onScenarioRestore={handleScenarioRestore}
        onScenarioDelete={handleScenarioDelete}
        onScenarioOpen={handleScenarioOpen}
        onFolderCreate={handleFolderCreate}
        onFolderDelete={handleFolderDelete}
        onFolderRename={handleFolderRename}
        clientColor={clientColor}
      />

      <ScenarioMaskModal
        open={isMaskModalOpen}
        onClose={handleCloseMaskModal}
        baseScenario={selectedScenarioForMask}
        scenarios={allScenarios}
        folders={folders}
      />

      <FolderCreateDialog
        open={isFolderCreateDialogOpen}
        onClose={handleFolderCreateDialogClose}
        onConfirm={handleFolderCreateConfirm}
        parentFolderName={
          folderCreateParentId
            ? folders.find((f) => f.folder_id === folderCreateParentId)?.name
            : null
        }
        clientColor={clientColor}
      />

      {/* New Scenario Popover */}
      <Popover
        open={Boolean(newScenarioAnchorEl)}
        anchorEl={newScenarioAnchorEl}
        onClose={handleCloseNewScenarioPopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          style: {
            boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <MenuItem onClick={handleFromInput} className={classes.selectMenuItem}>
          {t("actionbar.new_scenario_options.from_input")}
        </MenuItem>
        <MenuItem
          onClick={handleFromExisting}
          className={classes.selectMenuItem}
        >
          {t("actionbar.new_scenario_options.from_existing")}
        </MenuItem>
      </Popover>
    </>
  );
};

// Main component with provider
export default function CockpitMain() {
  return (
    <ScenarioDataProvider>
      <CockpitMainContent />
    </ScenarioDataProvider>
  );
}
